import { useCallback, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { AnefiDocumentType } from "@anefi/types";
import { useInvestForm } from "@anefi/contexts";
import { useFetchBanks, useFetchUser, usePutUser } from "@anefi/services";
import { useQueryClient } from "react-query";

import { StepOneFormValues } from "../NaturalPath/StepOneForm/StepOneForm.types";
import { getStepEightSchema } from "../../../../utils/form.utils";
import { getStepSevenSchema } from "../../../../utils/form.utils";
import { getStepFourSchema } from "../../../../utils/form.utils";
import { getStepOneSchema } from "../../../../utils/form.utils";
import { getStepThreeCompleteSchema } from "../../../../utils/form.utils";
import { getStepThreeSchema } from "../../../../utils/form.utils";
import { StepThreeFormValues } from "../NaturalPath/StepThreeForm/StepThreeForm.types";
import { StepFourFormValues } from "../NaturalPath/StepFourForm/StepFourForm.types";
import { StepSevenFormValues } from "../NaturalPath/StepSevenForm/StepSevenForm.types";
import { StepEightFormValues } from "../NaturalPath/StepEightForm/StepEightForm.types";
import useModals from "contexts/modals/modals.hooks";
import useAuth from "contexts/auth/auth.context.hooks";
import { transformValuesToRequest } from "./useCheckoutForms.helpers";
import { createSuccessNotification } from "utils/notifications.utils";

const useCheckoutForms = () => {
  const { setShowFirstInvestmentModal } = useModals();
  const [currentStep, setCurrentStep] = useState(1);
  const [documentCode, setDocumentCode] = useState<AnefiDocumentType>();
  const [formType, setFormType] = useState<string | undefined>();
  const isFormComplete = formType === "C";
  const { investForm, setInvestForm } = useInvestForm();
  const [showSelect, setShowSelect] = useState(false);
  const auth = useAuth();
  const { data: user } = useFetchUser(auth);
  const { mutateAsync: putUserMutate, reset: putUserReset } = usePutUser(auth);
  const { additionalInfo } = user ?? {};
  const { data: banks } = useFetchBanks();
  const queryClient = useQueryClient();
  const { uid } = auth;
  const { resetInvestFormContext } = useInvestForm();
  const spouseDependent = isFormComplete ? "01" : undefined;

  const nextStep = useCallback(async () => {
    setCurrentStep(currentStep + 1);
  }, [currentStep]);

  const onClose = useCallback(async () => {
    setShowFirstInvestmentModal(false);
  }, [setShowFirstInvestmentModal]);

  const stepOneFormMethods = useForm<StepOneFormValues>({
    mode: "onChange",
    resolver: yupResolver(getStepOneSchema()),
    defaultValues: {
      firstName: additionalInfo?.firstName,
      secondName: additionalInfo?.secondName,
      firstLastname: additionalInfo?.firstLastname,
      secondLastname: additionalInfo?.secondLastname,
      birthdate: user?.birthdate,
      nationalityCountryCode: additionalInfo?.nationalityCountryCode || "",
      document: user?.document,
      email: user?.email,
      homeAddressCountryCode: "001",
      documentTypeCode: "1",
      phoneTypeCode: additionalInfo?.phoneTypeCode ?? "3",
      phone: user?.phone?.number
    }
  });
  const { handleSubmit: handleStepOneSubmit } = stepOneFormMethods;

  const stepThreeFormMethods = useForm<StepThreeFormValues>({
    mode: "onChange",
    resolver: yupResolver(
      isFormComplete
        ? getStepThreeCompleteSchema(documentCode)
        : getStepThreeSchema()
    ),
    defaultValues: {
      maritalStatusCode: additionalInfo?.maritalStatusCode || "",
      spouseDependentTypeCode: "CONYUGAL",
      spouseName: additionalInfo?.spouseName,
      spouseDocument: additionalInfo?.spouseDocument
    }
  });
  const { handleSubmit: handleStepThreeSubmit } = stepThreeFormMethods;

  const stepFourFormMethods = useForm<StepFourFormValues>({
    mode: "onChange",
    resolver: yupResolver(getStepFourSchema()),
    defaultValues: {
      homeAddressProvinceCode: additionalInfo?.homeAddressProvinceCode || "",
      homeAddressCantonCode: additionalInfo?.homeAddressCantonCode || "",
      homeAddressMainStreet: additionalInfo?.homeAddressMainStreet,
      homePhoneType: "DOMICILIO",
      IshomeAddressFiscal: additionalInfo?.IshomeAddressFiscal === "S",
      // @ts-ignore TODO: correctly type the user with the additional Phone
      additionalPhone: user?.additionalPhone?.number
    }
  });
  const { handleSubmit: handleStepFourSubmit } = stepFourFormMethods;

  const stepSevenFormMethods = useForm<StepSevenFormValues>({
    mode: "onChange",
    resolver: yupResolver(getStepSevenSchema()),
    defaultValues: {
      monthlyExpenses: additionalInfo?.monthlyExpenses || "",
      totalIncome: additionalInfo?.totalIncome || "",
      payTaxes: "S",
      businessActivityTypeCode: additionalInfo?.businessActivityTypeCode,
      totalAssets: additionalInfo?.totalAssets || "",
      totalLiabilities: additionalInfo?.totalLiabilities || "",
      totalHeritage: additionalInfo?.totalHeritage || ""
    }
  });
  const { handleSubmit: handleStepSevenSubmit } = stepSevenFormMethods;

  const stepEightFormMethods = useForm<StepEightFormValues>({
    mode: "onChange",
    resolver: yupResolver(getStepEightSchema()),
    defaultValues: {
      bankAccountfinancialEntityCode:
        additionalInfo?.bankAccountfinancialEntityCode || "",
      bankAccountNumber: additionalInfo?.bankAccountNumber,
      bankAccountTypeCode: additionalInfo?.bankAccountTypeCode || ""
    }
  });
  const { handleSubmit: handleStepEightSubmit } = stepEightFormMethods;

  const submitStepOneFormHandler = useCallback<
    SubmitHandler<StepOneFormValues>
  >(
    async (values: StepOneFormValues) => {
      setInvestForm(form => {
        return { ...form, ...values };
      });
      nextStep();
    },
    [nextStep, setInvestForm]
  );

  const submitStepThreeFormHandler = useCallback<
    SubmitHandler<StepThreeFormValues>
  >(
    async (values: StepThreeFormValues) => {
      setInvestForm(form => {
        return { ...form, ...values };
      });
      nextStep();
    },
    [nextStep, setInvestForm]
  );

  const submitStepFourFormHandler = useCallback<
    SubmitHandler<StepFourFormValues>
  >(
    async (values: StepFourFormValues) => {
      setInvestForm(form => {
        return { ...form, ...values };
      });
      nextStep();
    },
    [nextStep, setInvestForm]
  );

  const submitStepSevenFormHandler = useCallback<
    SubmitHandler<StepSevenFormValues>
  >(
    async (values: StepSevenFormValues) => {
      setInvestForm(form => {
        return { ...form, ...values };
      });
      nextStep();
    },
    [nextStep, setInvestForm]
  );

  const submitStepEightFormHandler = useCallback<
    SubmitHandler<StepEightFormValues>
  >(
    async (values: StepEightFormValues) => {
      const { bankAccountfinancialEntityCode } = values;

      const bank = banks?.find(
        bank => bank.institutionCode === bankAccountfinancialEntityCode
      );

      const userFormValues = transformValuesToRequest({
        ...investForm,
        ...values
      });

      const { IshomeAddressFiscal } = userFormValues.additionalInfo;

      const newValues = {
        ...user,
        ...userFormValues,
        active: !!user?.active,

        additionalInfo: {
          ...user?.additionalInfo,
          ...userFormValues?.additionalInfo,
          spouseDependentTypeCode: spouseDependent,
          IshomeAddressFiscal,
          homePhoneType: "1",
          birthCountryCode: "001",
          bankAccountfinancialEntityTypeCode: bank?.institutionTypeCode
        },
        country: {
          id: 1,
          name: "ECU"
        }
      };

      await putUserMutate(newValues);
      onClose();
      createSuccessNotification(
        "Guardar información",
        "Has guardado tu información correctamente.",
        4000
      );
      putUserReset();
      resetInvestFormContext();
      queryClient.refetchQueries([uid, "bankReferences"]);
    },
    [
      banks,
      investForm,
      onClose,
      putUserMutate,
      putUserReset,
      queryClient,
      resetInvestFormContext,
      spouseDependent,
      uid,
      user
    ]
  );

  const handleSubmit = useCallback(() => {
    switch (currentStep) {
      case 1:
        handleStepOneSubmit(submitStepOneFormHandler)();
        break;

      case 2:
        handleStepThreeSubmit(submitStepThreeFormHandler)();
        break;

      case 3:
        handleStepFourSubmit(submitStepFourFormHandler)();
        break;

      case 4:
        handleStepSevenSubmit(submitStepSevenFormHandler)();
        break;

      case 5:
        handleStepEightSubmit(submitStepEightFormHandler)();
        break;

      default:
        break;
    }
  }, [
    currentStep,
    handleStepOneSubmit,
    submitStepOneFormHandler,
    handleStepThreeSubmit,
    submitStepThreeFormHandler,
    handleStepFourSubmit,
    submitStepFourFormHandler,
    handleStepSevenSubmit,
    submitStepSevenFormHandler,
    handleStepEightSubmit,
    submitStepEightFormHandler
  ]);

  return useMemo(
    () => ({
      handleSubmit,
      currentStep,
      setCurrentStep,
      onClose,
      stepOneFormMethods,
      stepThreeFormMethods,
      stepFourFormMethods,
      stepSevenFormMethods,
      stepEightFormMethods,
      setDocumentCode,
      setFormType,
      isFormComplete,
      showSelect,
      setShowSelect
    }),
    [
      handleSubmit,
      currentStep,
      onClose,
      stepOneFormMethods,
      stepThreeFormMethods,
      stepFourFormMethods,
      stepSevenFormMethods,
      stepEightFormMethods,
      isFormComplete,
      showSelect
    ]
  );
};

export default useCheckoutForms;
